<template lang="pug">
    div
        .form__title
            slot(name='title')
        form.form__body(action='javascript://' v-if='params')
            template(v-for="(example, index) in params.examples")
                .form__field(v-if="gameMode !== 'course' && gameMode !== 'generator-settings'")
                    label.form__label
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_numOfExamples') }}
                    .form__field-body.form__field-body--inline
                        cs-spin-input(
                            v-model="example.num"
                            :min="1"
                            :max="100"
                            @increase="example.num++"
                            @decrease="example.num--"
                        )
                        .remove-icon(v-if="index > 0" @click="removeExamples(index)")
                            svg-icon(name="cancel", size="x-large")
                .form__field(v-if="gameMode !== 'course'")
                    label.form__label
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_themeForExample', {example: null})}}
                    .form__field-body.form__field-body--inline.form__field-body--inline-md
                        .form__multiselect-container
                            cs-multiselect(
                                :multiple='false',
                                :options='themes',
                                :show-labels='false',
                                :allow-empty='false',
                                track-by='id',
                                :custom-label="getTranslatedName",
                                :placeholder="$i18n.t('trainer.trainer_gameFormSetting_chooseTheme')",
                                name='exampleTheme',
                                v-validate=`{ is_not: example.theme }`,
                                v-model='example.theme',
                            )
            .form__field
                .form__label
                .form__body
                    cs-button(
                        @click="addExamples()"
                        :disabled="params.examples.length > 100"
                    )
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_addExample') }}
            divider
            slot(name='parameters')
            slot(name='help')
            .form__buttons(v-if='!usersIds')
                cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
                template(v-if="gameMode === 'free-game'")
                    cs-button.form__button(color="blue", @click='saveGameMode')
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
                template(v-else='')
                    cs-button.form__button(color="blue", @click='saveGame')
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
            .form__buttons(v-else='')
                cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
                cs-button.form__button(color="blue", @click='saveGame')
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_saveDifference') }}
</template>

<script>
    import GameFormMixin from '../../../utils/trainer/GameFormMixin';
    import TrainerGames from '../../../config/trainer';
    import { mapState } from 'vuex';
    import { cloneDeep, camelCase, kebabCase } from 'lodash';
    import multiplayerFormMixin from '../../../utils/multiplayer/formMixin';
    import ScoutGameApi from '../../../api/games/scoutGameApi';

    export default {
        props: {
            slug: String,
            courseType: String,
            id: [Number, String],
            usersIds: [Array, String],
            homeworkId: [Number, String],
            gameMode: String,
            gameId: [Number, String],
            generatorGameIndex: Number,
            generatorSettings: Boolean,
            levelAnswerDuration: Number,
        },
        mixins: [GameFormMixin, multiplayerFormMixin],
        data() {
            return {
                themes: [],
                examples: [],
                params: null,
            };
        },
        async mounted() {
            if (this.gameMode === 'free-game') {
                const paramsArray = this.$store.state.trainer.paramsArray;
                if (
                    paramsArray &&
                    paramsArray[0] &&
                    paramsArray[0].slug === this.slug &&
                    this.$store.state.trainer.game?.courseType &&
                    this.courseType &&
                    this.courseType === this.$store.state.trainer.game.courseType
                ) {
                    this.paramsArray = cloneDeep(paramsArray);
                    this.$emit('playersNumber', this.paramsArray.length);
                    this.params = this.paramsArray[0];
                } else {
                    const courseTypeKebab = kebabCase(this.courseType);
                    const params = TrainerGames.games.find(
                        ({ slug, courseType }) => slug === this.slug && courseType === courseTypeKebab
                    ).parameters.default;
                    this.params = cloneDeep(params);
                }
            } else {
                this.setInitParams(this.getKey(this.usersIds));
            }
            await this.setThemes();
            this.params.examples.length || this.addExamples();
        },
        methods: {
            async setThemes() {
                this.themes = [];
                try {
                    this.themes = await ScoutGameApi.getThemes();
                } catch (error) {
                    this.$toasted.error(error);
                }
            },
            addExamples() {
                this.params.examples.push({
                    theme: cloneDeep(this.themes[0]),
                    num: 1,
                });
            },
            removeExamples(index) {
                this.params.examples.splice(index, 1);
            },
            getTranslatedName({ id }) {
                return this.translatedNameById[id];
            },
        },
        computed: {
            ...mapState({
                homework: state => state.trainer.homework,
                gamesParameters: state => state.trainer.gamesParameters,
                user: state => state.auth.user,
            }),
            translatedNameById() {
                return this.themes.reduce((acc, { id, name }) => {
                    acc[id] = this.$i18n.t('trainer.trainer_gameFormSetting_' + camelCase(name));
                    return acc;
                }, {});
            },
        },
    };
</script>

<style lang="scss" scoped>
    .form__title {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        margin-bottom: 25px;
    }

    .form__body {
        width: 100%;
        box-sizing: border-box;
        @include media($lg) {
            padding: 0 4.5%;
        }
    }

    .form__field {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        @include media($md) {
            padding: 0 2.5px;
            flex-direction: row;
            align-items: center;
        }
        @include media($lg) {
            padding: 0 10%;
        }
        @include media($xlg) {
            padding: 0 13%;
        }
    }
    .form__label {
        min-width: 0;
        font-size: 16px;
        line-height: 19px;
        margin-right: 20px;
        margin-bottom: 0.5rem;
        @include media($md) {
            width: 8em;
            margin-bottom: 0;
            text-align: right;
        }
        @include media($xlg) {
            max-width: none;
            min-width: 13em;
        }
    }

    .form__field-body {
        flex-grow: 5;
    }

    .form__field-body--inline {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .form__field-body--inline-md {
        flex-direction: column;
        @include media($md) {
            align-items: baseline;
            flex-direction: row;
            flex: 1 0;
        }
    }

    .remove-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        color: $x-color-black;
        &:hover {
            color: $hsl-color-red-62;
        }
    }

    .form__multiselect-container {
        width: 100%;
        @include media($md) {
            width: auto;
            flex: 1 1 auto;
        }
    }

    .form__multiselect-container:not(:last-child) {
        margin-bottom: 20px;
        @include media($md) {
            margin: 0 10px 0 0;
        }
    }

    .form__buttons {
        margin-top: 30px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        @include media($md) {
            flex-direction: row;
        }
    }

    .form__button {
        margin: 10px 50px;
        @include media($md) {
            margin: 0 10px;
        }
    }
</style>
